/* import __COLOCATED_TEMPLATE__ from './input.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import clsx from 'clsx';

/** @type {string} */
const CLASS_NAME = 'GLM-input';
const CLASS_NAME_CAPTION = `${CLASS_NAME}--caption`;
const CLASS_NAME_DISABLED = `${CLASS_NAME}--disabled`;
const CLASS_NAME_ERROR = `${CLASS_NAME}--error`;
const CLASS_NAME_FOCUSED = `${CLASS_NAME}--focused`;
const CLASS_NAME_READONLY = `${CLASS_NAME}--readonly`;

/** @type {object} */
const THEMES = {
  BLACK: 'black',
  WHITE: 'white',
};

const THEME_TO_CLASS_MAP = Object.keys(THEMES).reduce((accum, themeKey) => {
  const theme = THEMES[themeKey];
  accum[theme] = `${CLASS_NAME}--theme-${theme}`;
  return accum;
}, {});

/** @class */
class GlmInput extends Component {
  @tracked focus = false;

  get classNames() {
    const hasCaption = Boolean(this.args.caption);
    const isDisabled = Boolean(this.args.disabled);
    // Prevents classNames from being called multple times in the same computations when args.disabled is toggled
    // because that calls handleBlur because the element loses focus.
    const hasFocus = !isDisabled ? this.focus : false;

    return clsx(
      CLASS_NAME,
      this.args.class,
      hasCaption && CLASS_NAME_CAPTION,
      isDisabled && CLASS_NAME_DISABLED,
      this.hasError && CLASS_NAME_ERROR,
      hasFocus && CLASS_NAME_FOCUSED,
      this.args.readonly && CLASS_NAME_READONLY,
      this.theme,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  get canShowAffixes() {
    if (this.args.disableAffixes) {
      return false;
    }

    if (this.args.hideAffixesByDefault) {
      return Boolean(this.args.value || this.focus);
    }

    return true;
  }

  get hasError() {
    return Boolean(this.args.error);
  }

  get theme() {
    const { theme } = this.args;
    return THEME_TO_CLASS_MAP[theme || THEMES.BLACK];
  }

  /**
   * @param {InputEvent} e
   */
  @action
  handleInput({ target }) {
    this.args.oninput?.(target.value);
  }

  /**
   * @param {KeyboardEvent} e
   */
  @action
  handleKeyDown(e) {
    this.args.onkeydown?.(e);

    if (e.defaultPrevented) {
      return;
    }

    if (e.key === 'Enter' && !this.args.allowEnter) {
      e.preventDefault();
    }
  }

  /**
   * @param {MouseEvent} e
   */
  @action
  handleFocus(e) {
    this.focus = true;

    this.args.onfocus?.(e);
  }

  /**
   * @param {MouseEvent} e
   */
  @action
  handleBlur(e) {
    this.focus = false;

    this.args.onblur?.(e);
  }

  /**
   * @param {KeyboardEvent} e
   */
  @action
  handleKeyUp(e) {
    this.args.onkeyup?.(e);
  }

  /**
   * @param {MouseEvent} e
   */
  @action
  handleClick(e) {
    this.args.onclick?.(e);
  }
}

export default GlmInput;
